import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

import Button from '../../components/ui/Button';
import DashboardMetrics from './components/DashboardMetrics';
import QuoteRequestsTable from './components/QuoteRequestsTable';
import ProfileSection from './components/ProfileSection';
import ServiceAreasSection from './components/ServiceAreasSection';
import CertificationsSection from './components/CertificationsSection';
import MainNavigation from '../../components/ui/MainNavigation';

const LeverancierDashboard = () => {
  const { user, userProfile, loading } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (!loading && (!user || !['provider', 'superadmin']?.includes(userProfile?.user_type))) {
      navigate('/leverancier-login');
    }
  }, [user, userProfile, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'quotes', label: 'Offerteaanvragen', icon: 'FileText' },
    { id: 'profile', label: 'Profiel', icon: 'User' },
    { id: 'service-areas', label: 'Werkgebieden', icon: 'MapPin' },
    { id: 'certifications', label: 'Certificeringen', icon: 'Award' },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <DashboardMetrics />
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-heading font-semibold text-foreground mb-4">Recente Offerteaanvragen</h2>
              <QuoteRequestsTable limit={5} />
            </div>
          </div>
        );
      case 'quotes':
        return <QuoteRequestsTable limit={10} />;
      case 'profile':
        return <ProfileSection />;
      case 'service-areas':
        return <ServiceAreasSection />;
      case 'certifications':
        return <CertificationsSection />;
      default:
        return <DashboardMetrics />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MainNavigation />

      {/* Main Content with sidebar spacing */}
      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
        {/* Dashboard Header */}
        <div className="bg-card border-b border-border shadow-elevation-1 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground">
                {sidebarItems?.find((item) => item?.id === activeSection)?.label || 'Dashboard'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Welkom terug, {userProfile?.full_name || 'Leverancier'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" iconName="Bell" size="icon">
                <span className="sr-only">Notificaties</span>
              </Button>
              <Button variant="outline" iconName="Settings" size="icon">
                <span className="sr-only">Instellingen</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6">{renderContent()}</div>
      </main>
    </div>
  );
};

export default LeverancierDashboard;